import { FC } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Radio, RadioGroup } from '../../../components/radio-group/index';
import { Switch } from '../../../components/switch/index';
import {
  columnTitle,
  ColumnVariant,
  customizeSortVariantTitleMap,
  detailsColumns,
  DetailsSortVariant,
  ViewBy,
} from '../../../types/snapshot.types';

/** One view mode's worth of the tab: which columns are on, and what the rows are grouped by. */
export type ViewColumnsSettings = {
  columns: Record<ColumnVariant, boolean>;
  viewBy: Record<ViewBy, boolean>;
};

type DetailsViewColumnsTabProps = {
  onChangeColumn: (field: ColumnVariant, state: boolean) => void;
  onChangeViewBy: (field: ViewBy, state: boolean) => void;
  onSelectVariant: (variant: DetailsSortVariant) => void;
  selectedVariant: DetailsSortVariant;
  settings: ViewColumnsSettings;
};

export const DetailsViewColumnsTab: FC<DetailsViewColumnsTabProps> = ({
  onChangeColumn,
  onChangeViewBy,
  onSelectVariant,
  selectedVariant,
  settings,
}) => {
  const isAtLeastSelectedOneViewBy = settings.viewBy.stakeholders || settings.viewBy.type;

  return (
    <>
      <div className="flex flex-col gap-2 border-b border-b-gray-100 pb-4">
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="BY" />
        </span>

        <div className="w-fit rounded-3xl border border-gray-200 p-1">
          <RadioGroup
            onChange={onSelectVariant}
            value={selectedVariant}
            wrapperClassName="w-full grow flex-1"
          >
            {customizeSortVariantTitleMap.map(([graphKey, graphTitle]) => (
              <Radio
                className="h-8 !w-fit rounded-[32px] border-transparent bg-transparent !px-3 !py-2 text-xs font-[450] text-gray-700 transition-colors duration-200 ease-out data-[checked]:font-[550] data-[checked]:text-brand-25"
                key={graphKey}
                value={graphKey}
              >
                <span className="text-nowrap">
                  <AppFormattedMessage id={graphTitle} />
                </span>
              </Radio>
            ))}
          </RadioGroup>
        </div>
      </div>

      {selectedVariant === DetailsSortVariant.BY_STAKEHOLDER && (
        <div className="flex flex-col gap-4 border-b border-b-gray-100 pb-4">
          <span className="text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="VIEW_BY" />
          </span>

          <div className="mt-2 flex gap-2">
            <Switch
              checked={settings.viewBy.type}
              key={`${selectedVariant}view-by-__type_switch`}
              onChange={(state) => onChangeViewBy(ViewBy.TYPE, state)}
            />
            <span className="text-xs font-[450] text-gray-700">
              <AppFormattedMessage id="TYPE" />
            </span>
          </div>
          <div className="flex gap-2">
            <Switch
              checked={settings.viewBy.stakeholders}
              key={`${selectedVariant}view-by-stakeholders_switch`}
              onChange={(state) => onChangeViewBy(ViewBy.STAKEHOLDERS, state)}
            />
            <span className="text-xs font-[450] text-gray-700">
              <AppFormattedMessage id="STAKEHOLDERS" />
            </span>
          </div>

          {!isAtLeastSelectedOneViewBy && (
            <span className="text-xs font-[450] text-fireside-600">
              <AppFormattedMessage id="SELECT_AT_LEAST_ONE_VIEW_BY" />
            </span>
          )}
        </div>
      )}

      {/* No trailing divider: the design rules *between* sections, and the last one's border plus
          padding pushed the panel 1px past its scroll container, which showed a stray scrollbar. */}
      <div className="flex flex-col gap-4">
        <span className="text-xs font-[450] text-gray-500">
          <AppFormattedMessage id="COLUMNS" />
        </span>

        <div className="grid grid-flow-row grid-cols-2 gap-4">
          {detailsColumns[selectedVariant].map(
            (columnKey) =>
              columnTitle[columnKey] && (
                <div className="mt-2 flex gap-2" key={`${columnKey}_${selectedVariant}`}>
                  <Switch
                    checked={settings.columns[columnKey]}
                    onChange={(state) => onChangeColumn(columnKey, state)}
                  />
                  <span className="text-xs font-[450] text-gray-700">
                    <AppFormattedMessage id={columnTitle[columnKey]} />
                  </span>
                </div>
              ),
          )}
        </div>
      </div>
    </>
  );
};
